import {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {useUrlUpdater} from 'components/data/WithUrlUpdater'
import {SEARCH_ROUTE} from 'constants/index'
import {addDays} from 'date-fns/addDays'
import {getCurrencyCode, getLocaleCode} from 'modules/meta/selectors'
import {SearchUrlParams} from 'modules/search/types'
import {getLanguageTrackingInfo} from 'utils/localization/language'
import {buildSeoApPath} from 'utils/server/apSeo/getSeoAPPath'

import {BottomSheet} from '@vio/atlas/src/components/designSystem/BottomSheet'
import {Button} from '@vio/atlas/src/components/designSystem/Button'
import {Flag} from '@vio/atlas/src/components/designSystem/Flag'
import {Box} from '@vio/atlas/src/components/designSystem/helpers/Box'
import {InputSelect} from '@vio/atlas/src/components/designSystem/InputSelect'
import {PopUp} from '@vio/atlas/src/components/designSystem/PopUp'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {getCookie, setCookie} from '@vio/core/src/_web/utils/cookies'
import {filterAllowedCodes, isCodeAllowed} from '@vio/core/src/brand/utils'
import {getEnabledCurrencies} from '@vio/core/src/localization/business/currency'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {getCountryCodeFromLanguage} from '@vio/core/src/localization/services/country'
import availableLanguages from '@vio/core/src/localization/services/locale'

const generateCookieName = (browserLang: string, currentLocale: string) => {
  return `LanguageMismatchOverlay-${browserLang}-${currentLocale}`
}

const LanguageMismatchOverlay = ({hotelId = ''}: {hotelId?: string}) => {
  const {isMobile} = useDeviceLayout()
  const [isOpen, setIsOpen] = useState(false)
  const currencyCode = useSelector(getCurrencyCode)
  const {brand} = useBrand()
  const enabledCurrencies = filterAllowedCodes(
    getEnabledCurrencies(process.env.NEXT_PUBLIC_ENABLED_CURRENCIES),
    brand.currencyCodes
  )
  const languages = filterAllowedCodes(availableLanguages, brand.languageCodes)

  const browserLang = useMemo(() => {
    const lang = getLanguageTrackingInfo().browserLang
    return lang && isCodeAllowed(lang, brand.languageCodes) ? lang : null
  }, [brand.languageCodes])

  const localeCode = useSelector(getLocaleCode)
  const [locale, setLocale] = useState<string>(browserLang)

  const [currency, setCurrency] = useState<string>(currencyCode)
  const {formatMessage} = useIntl()
  const {navigateWithParameters} = useUrlUpdater()

  useEffect(() => {
    if (browserLang && browserLang !== localeCode) {
      const hasSeenLocale = Boolean(
        getCookie(generateCookieName(browserLang, localeCode))
      )
      if (!hasSeenLocale) {
        setIsOpen(true)
        setCookie(generateCookieName(browserLang, localeCode), 'true', {
          expires: addDays(new Date(), 1)
        })
      }
    }
  }, [browserLang, localeCode])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleLanguageChange = (languageCode: string) => {
    setLocale(languageCode)
  }

  const handleCurrencyChange = (currencyCode: string) => {
    setCurrency(currencyCode)
  }

  const handleSubmit = useCallback(() => {
    const apBasePath = buildSeoApPath({hotelId, locale})
    const basePath = hotelId ? apBasePath : SEARCH_ROUTE
    const params: Partial<SearchUrlParams> = hotelId
      ? {
          currency,
          forceCurrencyChange: '1'
        }
      : {
          lang: locale,
          forceLanguageChange: '1',
          currency,
          forceCurrencyChange: '1'
        }

    navigateWithParameters(params, {
      basePath
    })
  }, [locale, navigateWithParameters, currency, hotelId])

  const renderContent = () => {
    return (
      <>
        <Box textAlign="center">
          <Text variant="bodyS">
            <FormattedMessageWrapper
              id="languageMismatchOverlay.description"
              defaultMessage="Select your currency and language to see the most relevant prices, details, and settings."
            />
          </Text>
        </Box>
        <InputSelect
          onChange={handleCurrencyChange}
          value={currency}
          label={formatMessage({
            id: 'languageMismatchOverlay.chooseYourCurrency',
            defaultMessage: 'Choose your currency'
          })}
        >
          {enabledCurrencies.map(item => {
            return (
              <option
                key={item.code}
                value={item.code}
              >{`${item.code} - ${item.symbol}`}</option>
            )
          })}
        </InputSelect>
        <InputSelect
          onChange={handleLanguageChange}
          prefix={<Flag countryCode={getCountryCodeFromLanguage(locale)} />}
          value={locale}
          label={formatMessage({
            id: 'languageMismatchOverlay.chooseYourLanguage',
            defaultMessage: 'Choose your language'
          })}
        >
          {languages.map(item => {
            return (
              <option key={item.code} value={item.code}>
                {item.displayName}
              </option>
            )
          })}
        </InputSelect>
      </>
    )
  }

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={isOpen}
        noPaddings
        snapPoints={{top: 'nearFull', middle: 0.85}}
        navBarProps={{
          withDivider: false,
          variant: 'transparent',
          floating: true,
          centerContent: (
            <Text variant="titleM" tag="p">
              <FormattedMessageWrapper
                defaultMessage={'Are you in the right place?'}
                id="languageMismatchOverlay.title"
              />
            </Text>
          )
        }}
        onClose={handleClose}
        footerContent={
          <Box padding="s400" paddingLeft="s500" paddingRight="s500">
            <Button variant="primary" fullWidth onClick={handleSubmit}>
              <Text variant="labelM">
                <FormattedMessageWrapper
                  id="continue"
                  defaultMessage="Continue"
                />
              </Text>
            </Button>
          </Box>
        }
      >
        <Box
          display="flex"
          flexDirection="column"
          marginTop="s900"
          padding="s500"
          gap="s600"
        >
          {renderContent()}
        </Box>
      </BottomSheet>
    )
  }

  return (
    <PopUp
      isOpen={isOpen}
      width={324}
      noPaddings
      navBarProps={{
        withDivider: false,
        variant: 'transparent',
        floating: true,
        centerContent: (
          <Text variant="titleM" tag="p">
            <FormattedMessageWrapper
              defaultMessage={'Are you in the right place?'}
              id="languageMismatchOverlay.title"
            />
          </Text>
        )
      }}
      closeAction={handleClose}
    >
      <Box
        display="flex"
        flexDirection="column"
        paddingTop="s900"
        paddingLeft="s500"
        paddingRight="s500"
        height="100%"
        justifyContent="space-between"
      >
        <Box display="flex" flexDirection="column" gap="s600" paddingTop="s300">
          {renderContent()}
        </Box>
        <Box paddingTop="s400" paddingBottom="s400">
          <Button variant="primary" fullWidth onClick={handleSubmit}>
            <Text variant="labelM">
              <FormattedMessageWrapper
                id="continue"
                defaultMessage="Continue"
              />
            </Text>
          </Button>
        </Box>
      </Box>
    </PopUp>
  )
}

export default LanguageMismatchOverlay
